/*
 * Copyright (c) 2024 Paul Ostrowski. All rights reserved.
 *
 * This software is the proprietary information of Paul Ostrowski.
 * Unauthorized copying, modification, or distribution of this code, via any medium, is strictly prohibited.
 * This code is proprietary and confidential.
 *
 * For licensing inquiries, contact Paul Ostrowski at postrowski@gmail.com.
*/

import React from 'react';
import MultiSelectString from 'components/common/MultiSelectString';
import Open5eDocument from "model/formats/Open5eDocument";

interface PropertySetProps<T extends string | Open5eDocument> {
  name: string;
  values: string | string[] | Record<string, any> | null;
  valuesArray?: T[];
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  options?: T[];
  allowCustom?: boolean;
  showClear?: boolean;
  onSave?: (selectedValues: string) => void;
  onSaveArray?: (selectedValuesArray: T[]) => void;
  alwaysShow?: boolean;
  columns?: number;
  showGrouping?: boolean;
  className?: string;
  maxSelections?: number;
  noWrap?: boolean;
  displayFunction?: (option: T) => string | JSX.Element;
  getIdFunction?: (option: T) => string;
  filterFunction?: (option: T) => boolean;
  onClickOutside: (event: MouseEvent) => void;
}

const PropertySet = <T extends string | Open5eDocument>({
  name,
  values,
  valuesArray,
  isOpen,
  setIsOpen,
  options,
  allowCustom = false,
  showClear = false,
  onSave,
  onSaveArray,
  alwaysShow = false,
  columns = 2,
  showGrouping = true,
  className = '',
  maxSelections,
  noWrap = false,
  displayFunction,
  getIdFunction,
  filterFunction,
  onClickOutside
}: PropertySetProps<T>) => {
  let content: React.ReactNode = '';

  if (valuesArray && displayFunction) {
    content = valuesArray.map((o, index) => (
      <React.Fragment key={index}>
        {displayFunction(o)}
        {index < valuesArray.length - 1 && ', '}
      </React.Fragment>
    ));
  } else if (Array.isArray(values)) {
    // Handle array of values
    content = values.filter(Boolean).join(', ');
  } else if (typeof values === 'object' && values !== null) {
    // Handle object of values
    const entries = Object.entries(values)
                          .map(([key, value]) => `${key.charAt(0).toUpperCase() + key.slice(1)}: ${value}`);
    content = entries.join(', ');
  } else if (typeof values === 'string') {
    // Handle string values directly
    content = values;
  }

  // Check if content is empty (for string or empty array/object)
  if (!content && !valuesArray && !alwaysShow) return null;

  if (options) {
    content = (
      <MultiSelectString
        values={content as string}
        valuesArray={valuesArray}
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        options={options}
        allowCustom={allowCustom}
        showClear={showClear}
        onSave={onSave}
        onSaveArray={onSaveArray}
        columns={columns}
        className={className}
        showGrouping={showGrouping}
        maxSelections={maxSelections}
        noWrap={noWrap}
        displayFunction={displayFunction}
        getIdFunction={getIdFunction}
        filterFunction={filterFunction}
        onClickOutside={onClickOutside}
      />
    );
  }

  return (
    <div className={`property-line ${className}`}>
      {name && <h4>{name}</h4>}
      <span>{content}</span>
    </div>
  );
};

export default PropertySet;
